/**
 * Lift2Fit — the public legal shell.
 *
 * Rendered signed out and outside the app shell: consent happens before an
 * account exists, so these pages must never sit behind a login. They share the
 * marketing site's chrome (logo, footer) and the app's theme tokens.
 */
import type { ReactNode } from 'react';
import { Footer } from '@/components/Footer';
import { Logo } from '@/components/Logo';
import { Link } from '../router';
import {
  BRAND_LINE,
  DOMAIN,
  LEGAL_DOCS,
  LEGAL_DOC_ORDER,
  POLICY_VERSION,
  effectiveDateLabel,
  type LegalDocId,
} from './policy';

export function LegalLayout({
  title,
  currentId,
  children,
}: {
  title: string;
  currentId: LegalDocId | null;
  children: ReactNode;
}) {
  return (
    <div className="l2f-app flex min-h-screen flex-col">
      <header className="border-b border-hairline bg-surface">
        <div className="mx-auto flex w-full max-w-[980px] flex-wrap items-center justify-between gap-3 px-4 py-4 md:px-8">
          <Link to="/" aria-label="Lift2Fit home" className="flex items-center gap-2 text-ink">
            <Logo variant="horizontal" className="h-7 w-[132px]" />
          </Link>
          <nav aria-label="Legal documents" className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {LEGAL_DOC_ORDER.map((id) => {
              const meta = LEGAL_DOCS[id];
              const current = meta.id === currentId;
              return (
                <Link
                  key={meta.id}
                  to={meta.path}
                  aria-current={current ? 'page' : undefined}
                  className={
                    current
                      ? 'text-[12px] font-bold uppercase tracking-[0.08em] text-accent'
                      : 'text-[12px] font-bold uppercase tracking-[0.08em] text-ink-muted hover:text-accent'
                  }
                >
                  {meta.nav}
                </Link>
              );
            })}
            <Link
              to="/signup"
              data-testid="legal-back-signup"
              className="inline-flex h-9 items-center border border-hairline px-3 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
            >
              Back to sign up
            </Link>
          </nav>
        </div>
      </header>

      <main id="main" className="mx-auto w-full max-w-[820px] flex-1 px-4 py-10 md:px-8">
        <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-accent">Legal</p>
        <h1 className="mt-2 font-display text-h2 font-black uppercase leading-[1.05] tracking-[-0.01em] text-ink md:text-display-2">
          {title}
        </h1>
        <p
          className="mt-3 text-[13px] text-ink-muted"
          data-testid="legal-meta"
          data-policy-version={POLICY_VERSION}
        >
          Version {POLICY_VERSION} · Effective {effectiveDateLabel()} · {DOMAIN}
        </p>
        <p className="mt-1 font-display text-[15px] font-black tracking-[-0.01em] text-ink">{BRAND_LINE}</p>

        <div className="mt-8 flex flex-col gap-8" data-testid="legal-body">
          {children}
        </div>

        <nav className="mt-12 flex flex-col gap-2 border-t border-hairline pt-6" aria-label="Other legal documents">
          <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-ink-muted">The other documents</p>
          {LEGAL_DOC_ORDER.filter((id) => id !== currentId).map((id) => {
            const meta = LEGAL_DOCS[id];
            return (
              <Link key={meta.id} to={meta.path} className="text-[14px] font-bold text-accent hover:underline">
                {meta.title}
              </Link>
            );
          })}
        </nav>
      </main>

      <Footer />
    </div>
  );
}

export function LegalSection({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section id={id} aria-labelledby={`${id}-heading`} className="flex flex-col gap-3">
      <h2
        id={`${id}-heading`}
        className="font-display text-[20px] font-black uppercase tracking-[-0.01em] text-ink"
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

export function LegalP({ children }: { children: ReactNode }) {
  return <p className="text-[15px] leading-relaxed text-ink-muted">{children}</p>;
}

export function LegalList({ items }: { items: ReactNode[] }) {
  return (
    <ul className="flex flex-col gap-2">
      {items.map((item, index) => (
        <li key={index} className="flex gap-2 text-[15px] leading-relaxed text-ink-muted">
          <span aria-hidden="true" className="mt-[9px] h-[6px] w-[6px] shrink-0 bg-accent" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export function LegalCallout({
  tone = 'info',
  title,
  children,
}: {
  tone?: 'info' | 'warning';
  title: string;
  children: ReactNode;
}) {
  return (
    <aside
      className={
        tone === 'warning'
          ? 'border-l-4 border-l-accent bg-page-subtle px-4 py-3'
          : 'border-l-4 border-l-hairline bg-page-subtle px-4 py-3'
      }
    >
      <p className="text-[12px] font-bold uppercase tracking-[0.1em] text-ink">{title}</p>
      <div className="mt-1 flex flex-col gap-2 text-[14px] leading-relaxed text-ink-muted">{children}</div>
    </aside>
  );
}

export interface LegalTableRow {
  cells: ReactNode[];
}

export function LegalTable({ head, rows }: { head: string[]; rows: LegalTableRow[] }) {
  return (
    <div className="overflow-x-auto border border-hairline">
      <table className="w-full border-collapse text-left text-[13px]">
        <thead>
          <tr className="bg-page-subtle">
            {head.map((cell) => (
              <th key={cell} className="border-b border-hairline px-3 py-2 font-bold uppercase tracking-[0.06em] text-ink">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index} className="align-top">
              {row.cells.map((cell, cellIndex) => (
                <td key={cellIndex} className="border-b border-hairline px-3 py-2 text-ink-muted">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
